import {orderBy, uniqBy} from 'lodash-es';
import {createSelector} from '@reduxjs/toolkit';
import {
    facilitiesAdapter,
    filteredFacilitiesAdapter,
} from './facilities-adapters';

/** @namespace Facilities.Selectors */

/**
 * Facility selectors generated by the facilitiesAdapter.
 * @property {Function} selectAllFacilities - Returns a collection of all facilities
 * @property {Function} selectFacilityIds - Returns a collection of facility ids
 * @property {Function} selectFacilityEntities - Returns a collection of facilities keyed by id
 * @property {Function} selectTotalFacilities - Returns the total number of facilities as a number
 *
 * @constant FacilitySelectors
 * @see https://redux-toolkit.js.org/api/createEntityAdapter#selector-functions
 */
export const {
    selectAll: selectAllFacilities,
    selectIds: selectFacilityIds,
    selectEntities: selectFacilityEntities,
    selectTotal: selectTotalFacilities,
} = facilitiesAdapter.getSelectors((state) => state.facilities.facilities);

/**
 * Canonical Facility selectors generated by the facilitiesAdapter.
 * @property {Function} selectAllCanonicalFacilities - Returns a collection of all facilities
 * @property {Function} selectCanonicalFacilityIds - Returns a collection of facility ids
 * @property {Function} selectCanonicalFacilityEntities - Returns a collection of facilities keyed by id
 * @property {Function} selectTotalCanonicalFacilities - Returns the total number of facilities as a number
 *
 * @constant CanonicalFacilitySelectors
 * @see https://redux-toolkit.js.org/api/createEntityAdapter#selector-functions
 */
export const {
    selectAll: selectAllCanonicalFacilities,
    selectIds: selectCanonicalFacilityIds,
    selectEntities: selectCanonicalFacilityEntities,
    selectTotal: selectTotalCanonicalFacilities,
} = facilitiesAdapter.getSelectors(
    (state) => state.facilities.canonicalFacilities
);

/**
 * Selector for a facility by id. Only to be used with the selectFacilityById select so that we can return an empty object if the facility is not found.  This is due to not being able to set a default return value for entityAdapter selectors
 * @private
 */
const {selectById: selectByIdEntitySelector} = facilitiesAdapter.getSelectors(
    (state) => state.facilities.facilities
);

/**
 * Filtered facility selectors generated by the filteredFacilitiesAdapter.
 * @property {Function} selectAllFilteredFacilities - Returns a collection of all filtered facilities
 * @property {Function} selectFilteredFacilityById - Returns a filtered facility by id
 * @property {Function} selectFilteredFacilityIds - Returns a collection of filtered facility ids
 * @property {Function} selectFilteredFacilityEntities - Returns a collection of filtered facilities keyed by id
 * @property {Function} selectTotalFilteredFacilities - Returns the total number of filtered facilities as a number
 *
 * @constant FilteredFacilitySelectors
 * @see https://redux-toolkit.js.org/api/createEntityAdapter#selector-functions`
 */
export const {
    selectAll: selectAllFilteredFacilities,
    selectById: selectFilteredFacilityById,
    selectIds: selectFilteredFacilityIds,
    selectEntities: selectFilteredFacilityEntities,
    selectTotal: selectTotalFilteredFacilities,
} = filteredFacilitiesAdapter.getSelectors(
    (state) => state.facilities.filteredFacilities
);

/**
 * Selector for a facility by id.  Returns an empty object if the facility is not found.
 * @param {TRootState} state - Redux state
 * @param {Number} id - Facility id
 * @example selectFacilityById(state, 1)
 * @returns {TFacility}
 */
export const selectFacilityById = createSelector(
    [(state) => state, (state, id) => id],
    (state, id) => selectByIdEntitySelector(state, id) || {}
);

/**
 * Selector for facility cities.  Returns a collection of unique cities from the facilities collection.   Default sort order is ascending by name.
 * @param {TRootState} state - Redux state
 * @param {String} order - Sort order
 * @example selectFacilityCities(state, 'desc')
 */
export const selectFacilityCities = createSelector(
    [selectAllFacilities, ({_facilities}, order) => order],
    (facilities, order = 'asc') => {
        const cities = uniqBy(
            facilities.map(({cityID, city}) => ({id: cityID, name: city})),
            'name'
        );

        return orderBy(cities, 'name', order);
    }
);

/**
 * Selector for facilities by city.  Returns a collection of facilities that match the provided city id.
 * @param {TRootState} state - Redux state
 * @param {Number} cityId - City id
 * @example selectFacilitiesByCity(state, 1)
 */
export const selectFacilitiesByCity = createSelector(
    [selectAllFacilities, ({_facilities}, cityId) => cityId],
    (facilities, cityId) => {
        return facilities.filter(({cityID}) => cityId === cityID);
    }
);

/**
 * Selector for facilities by id.  Returns a collection of facilities that match the provided ids.
 * @param {TRootState} state - Redux state
 * @param {Array<Number>} entityIds - Array of facility ids
 * @example selectFacilitiesById(state, [1, 2, 3])
 */
export const selectFacilitiesById = createSelector(
    [selectAllFacilities, ({_facilities}, entityIds) => entityIds],
    (facilities, entityIds) => {
        const formattedEntityIds = entityIds.map((id) => parseInt(id, 10));

        return facilities.filter(({id}) =>
            formattedEntityIds.includes(parseInt(id, 10))
        );
    }
);

export const selectFilteredFacilitiesById = createSelector(
    [selectAllFilteredFacilities, ({_facilities}, entityIds) => entityIds],
    (facilities, entityIds) => {
        const formattedEntityIds = entityIds.map((id) => parseInt(id, 10));

        return facilities.filter(({id}) =>
            formattedEntityIds.includes(parseInt(id, 10))
        );
    }
);

/**
 * Selects the first facility from the filtered facilities collection in the store.
 * @function selectFirstFilteredFacility
 * @param {TRootState} state - The state object from the redux store
 * @returns {TFacility|object} - Memoized first facility
 */
export const selectFirstFilteredFacility = createSelector(
    selectAllFilteredFacilities,
    (facilities) => facilities?.[0] || {}
);

/**
 * Selector for the selected facility id.  If no facility is selected, the first facility in the filtered facilities collection is selected.
 * @param {TRootState} state - The state object from the redux store
 * @return {Number} - The id of the selected facility
 */
export const selectSelectedFacilityId = (state) => {
    return state.facilities.selectedFacilityId;
};

/**
 * Selector for the selected facility.  If no facility is selected, the first facility in the filtered facilities collection is selected.
 * @param {RootState} state - The state object from the redux store
 * @return {Facility} - The selected facility
 */
export const selectSelectedFacility = createSelector(
    [selectSelectedFacilityId, (state) => state],
    (id, state) => selectFilteredFacilityById(state, id) || {}
);

/**
 * Selector for facility search filter options
 * @return {TFacility|object} - The selected search options
 */
export const selectFilterOptions = (state) => state.facilities.searchFilters;

/**
 * Selector for applied facility search filters
 * @return {TFacility|object} - The selected search options
 */
export const selectAppliedFilters = (state) => state.facilities.appliedFilters;

/**
 * A selector function to select facilities by canonical ID.
 *
 * @param {Array} allFacilities - An array of all facilities.
 * @param {string} canonicalId - The canonical ID of the facility to select.
 *
 * @returns {object|null} - The selected facility object, or null if not found.
 */
export const selectFacilitiesByCanonicalId = createSelector(
    [selectAllFacilities, (_, canonicalId) => canonicalId],
    (facilities, canonicalId) => {
        return facilities.map(
            (facility) => facility.canonicalId === canonicalId
        );
    }
);

/**
 * Returns an array of canonical facilities filtered by the provided entity IDs.
 *
 * @function
 * @name selectCanonicalFacilitiesById
 * @param {TRootState}  - The default state object.
 * @param {Number[]} entityIds - An array of entity IDs to filter the canonical facilities by.
 * @returns {TCanonicalFacility[]} An array of canonical facilities filtered by the provided entity IDs.
 */
export const selectCanonicalFacilitiesById = createSelector(
    [selectAllCanonicalFacilities, ({_facilities}, entityIds) => entityIds],
    (facilities, entityIds) => {
        const formattedEntityIds = entityIds.map((id) => parseInt(id, 10));

        return facilities.filter(({id}) =>
            formattedEntityIds.includes(parseInt(id, 10))
        );
    }
);
